import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { fetchHoursResourcePlan } from "../../../api/hour";

const toDateInputValue = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const startOfWeek = (date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

const endOfWeek = (date) => {
  const d = startOfWeek(date);
  d.setDate(d.getDate() + 6);
  return d;
};

const statusClassName = (status) => {
  switch (status) {
    case "clocked_in":
      return "bg-emerald-100 text-emerald-800";
    case "available":
      return "bg-blue-100 text-blue-800";
    case "planned":
      return "bg-sky-100 text-sky-800";
    case "absent":
      return "bg-amber-100 text-amber-800";
    case "unavailable":
      return "bg-rose-100 text-rose-800";
    default:
      return "bg-slate-100 text-slate-700";
  }
};

export const HoursResourcePlan = () => {
  const { t } = useTranslation();
  const [dateFrom, setDateFrom] = useState(() => toDateInputValue(startOfWeek(new Date())));
  const [dateTo, setDateTo] = useState(() => toDateInputValue(endOfWeek(new Date())));
  const [selectedDate, setSelectedDate] = useState(() => toDateInputValue(new Date()));
  const [resourcePlan, setResourcePlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setError(null);

    fetchHoursResourcePlan({ date_from: dateFrom, date_to: dateTo })
      .then((res) => {
        if (!active) return;
        const data = res?.data?.data || null;
        setResourcePlan(data);
        if (data?.days?.length) {
          setSelectedDate((current) =>
            data.days.some((day) => day.date === current) ? current : data.days[0].date
          );
        }
      })
      .catch(() => {
        if (!active) return;
        setResourcePlan(null);
        setError(t("hours.resource_plan.load_error"));
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [dateFrom, dateTo, t]);

  const days = useMemo(() => resourcePlan?.days || [], [resourcePlan]);
  const selectedDay = useMemo(
    () => days.find((day) => day.date === selectedDate) || days[0],
    [days, selectedDate]
  );

  const rangeStats = useMemo(() => {
    const initial = {
      employees: resourcePlan?.totals?.employees || 0,
      available: 0,
      clocked_in: 0,
      absent: 0,
      scheduled_hours: 0,
    };

    return days.reduce((acc, day) => ({
      employees: acc.employees,
      available: acc.available + (day.stats?.available || 0),
      clocked_in: acc.clocked_in + (day.stats?.clocked_in || 0),
      absent: acc.absent + (day.stats?.absent || 0),
      scheduled_hours: acc.scheduled_hours + (day.stats?.scheduled_hours || 0),
    }), initial);
  }, [days, resourcePlan?.totals?.employees]);

  return (
    <DashboardHeader
      title={t("hours.resource_plan.title")}
      subtitle={t("hours.resource_plan.subtitle")}
    >
      <div className="space-y-6 px-4 py-4">
        <div className="rounded-md bg-white p-4 shadow">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {t("hours.resource_plan.date_from")}
              </label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                {t("hours.resource_plan.date_to")}
              </label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
              <div className="font-semibold">{t("hours.resource_plan.access_title")}</div>
              <div className="mt-1">{t("hours.resource_plan.access_body")}</div>
            </div>
          </div>
        </div>

        {isLoading ? (
          <ResourceLoading />
        ) : error ? (
          <div className="rounded-md bg-white p-6 text-sm text-rose-600 shadow">{error}</div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <StatCard label={t("hours.resource_plan.stats.employees")} value={rangeStats.employees} />
              <StatCard label={t("hours.resource_plan.stats.available_days")} value={rangeStats.available} />
              <StatCard label={t("hours.resource_plan.stats.clocked_in")} value={rangeStats.clocked_in} />
              <StatCard label={t("hours.resource_plan.stats.scheduled_hours")} value={`${rangeStats.scheduled_hours.toFixed(1)}h`} />
            </div>

            <div className="overflow-hidden rounded-md bg-white shadow">
              <div className="border-b border-gray-200 px-4 py-4">
                <h3 className="text-lg font-semibold text-gray-900">{t("hours.resource_plan.days_title")}</h3>
                <p className="text-sm text-gray-500">{t("hours.resource_plan.days_subtitle")}</p>
              </div>
              <div className="overflow-x-auto px-4 py-4">
                <div className="flex min-w-max gap-2">
                  {days.map((day) => (
                    <button
                      key={day.date}
                      type="button"
                      onClick={() => setSelectedDate(day.date)}
                      className={`rounded-md border px-3 py-2 text-left text-sm ${
                        selectedDay?.date === day.date
                          ? "border-blue-500 bg-blue-50 text-blue-900"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <div className="font-semibold">{formatDate(day.date)}</div>
                      <div className="mt-1 text-xs text-gray-500">
                        {t("hours.resource_plan.day_stats", {
                          available: day.stats?.available || 0,
                          employees: day.stats?.employees || 0,
                        })}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="overflow-hidden rounded-md bg-white shadow">
              <div className="border-b border-gray-200 px-4 py-4">
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {selectedDay ? formatDate(selectedDay.date) : t("hours.resource_plan.no_day")}
                    </h3>
                    <p className="text-sm text-gray-500">{t("hours.resource_plan.employee_overview")}</p>
                  </div>
                  {selectedDay?.stats ? (
                    <div className="flex flex-wrap gap-2 text-xs">
                      <SummaryPill label={t("hours.resource_plan.status.available")} value={selectedDay.stats.available} />
                      <SummaryPill label={t("hours.resource_plan.status.clocked_in")} value={selectedDay.stats.clocked_in} />
                      <SummaryPill label={t("hours.resource_plan.status.absent")} value={selectedDay.stats.absent} />
                      <SummaryPill label={t("hours.resource_plan.status.unavailable")} value={selectedDay.stats.unavailable} />
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <TableHead>{t("hours.resource_plan.table.employee")}</TableHead>
                      <TableHead>{t("hours.resource_plan.table.status")}</TableHead>
                      <TableHead>{t("hours.resource_plan.table.department")}</TableHead>
                      <TableHead align="right">{t("hours.resource_plan.table.scheduled")}</TableHead>
                      <TableHead align="right">{t("hours.resource_plan.table.shift")}</TableHead>
                      <TableHead>{t("hours.resource_plan.table.note")}</TableHead>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {(selectedDay?.users || []).length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-4 py-4 text-sm text-gray-500">
                          {t("hours.resource_plan.empty")}
                        </td>
                      </tr>
                    ) : (
                      selectedDay.users.map((row) => (
                        <tr key={row.user?.id}>
                          <td className="px-4 py-3 text-sm">
                            <div className="font-medium text-gray-900">
                              {row.user?.displayTitle || row.user?.name || t("hours.resource_plan.unknown_employee")}
                            </div>
                            <div className="text-xs text-gray-500">{row.user?.email}</div>
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusClassName(row.status)}`}>
                              {t(`hours.resource_plan.status.${row.status}`)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {row.user?.department?.displayTitle || "—"}
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-900">
                            {(row.scheduled_hours || 0).toFixed(1)}h
                          </td>
                          <td className="px-4 py-3 text-right text-sm text-gray-900">
                            {(row.shift_hours || 0).toFixed(1)}h
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {row.absence?.type?.displayTitle || row.absence?.type?.title || row.absence?.type?.name || row.open_punch?.clock_in_at || "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardHeader>
  );
};

const formatDate = (value) =>
  new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  }).format(new Date(`${value}T00:00:00`));

const StatCard = ({ label, value }) => (
  <div className="rounded-md bg-white p-4 shadow">
    <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-1 text-2xl font-semibold text-slate-900">{value}</div>
  </div>
);

const SummaryPill = ({ label, value }) => (
  <span className="rounded-full bg-slate-100 px-3 py-1 font-medium text-slate-700">
    {label}: {value}
  </span>
);

const TableHead = ({ children, align = "left" }) => (
  <th
    className={`px-4 py-3 ${align === "right" ? "text-right" : "text-left"} text-xs font-medium uppercase tracking-wider text-gray-500`}
  >
    {children}
  </th>
);
